import type React from "react";
import { useCallback, useMemo } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import clsx from "clsx";
import { isHtmlContentEmpty, sanitizeHtml } from "../../utils/htmlSanitizer";
import { Text } from "../Text";
import styles from "./RichTextEditor.module.scss";

export interface RichTextEditorProps {
  id?: string;
  label?: string;
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  helperText?: string;
  errors?: string[];
  disabled?: boolean;
  readOnly?: boolean;
  minHeight?: number;
  maxHeight?: number;
  className?: string;
  labelClassName?: string;
  allowedTags?: string[];
  allowedAttributes?: Record<string, string[]>;
}

// Default allowed HTML tags and attributes for security
const DEFAULT_ALLOWED_TAGS = [
  "p",
  "br",
  "strong",
  "em",
  "u",
  "s",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "ul",
  "ol",
  "li",
  "blockquote",
  "a",
];

const DEFAULT_ALLOWED_ATTRIBUTES = {
  a: ["href", "title", "target", "rel"],
  "*": ["class"],
};

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  id,
  label,
  value = "",
  onChange,
  onBlur,
  placeholder = "Start typing...",
  helperText,
  errors,
  disabled = false,
  readOnly = false,
  minHeight = 200,
  maxHeight = 400,
  className,
  labelClassName,
  allowedTags = DEFAULT_ALLOWED_TAGS,
  allowedAttributes = DEFAULT_ALLOWED_ATTRIBUTES,
}) => {
  const editorId =
    id || `rich-text-editor-${Math.random().toString(36).substr(2, 9)}`;
  const hasError = errors && errors.length > 0;

  // Sanitize HTML content for security
  const sanitizeContent = useCallback(
    (content: string): string => {
      return sanitizeHtml(content, {
        ALLOWED_TAGS: allowedTags,
        ALLOWED_ATTR: allowedAttributes,
        KEEP_CONTENT: true,
      });
    },
    [allowedTags, allowedAttributes]
  );

  // Quill configuration
  const quillModules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ["bold", "italic", "underline", "strike"],
          [{ list: "ordered" }, { list: "bullet" }],
          ["blockquote"],
        ],
      },
      clipboard: {
        // toggle to add extra line breaks when pasting HTML:
        matchVisual: false,
      },
    }),
    []
  );

  const quillFormats = useMemo(
    () => [
      "header",
      "bold",
      "italic",
      "underline",
      "strike",
      "list",
      "blockquote",
    ],
    []
  );

  const handleEditorChange = useCallback(
    (content: string) => {
      // Check if content is effectively empty and return empty string if so
      if (isHtmlContentEmpty(content)) {
        onChange?.("");
        return;
      }

      // Don't sanitize on every change to avoid interfering with Quill's formatting
      // Sanitization will happen on blur or when content is saved
      onChange?.(content);
    },
    [onChange]
  );

  const handleEditorBlur = useCallback(() => {
    // Check if content is effectively empty first
    if (value && isHtmlContentEmpty(value)) {
      onChange?.("");
    } else if (value) {
      // Sanitize content when editor loses focus
      const sanitizedContent = sanitizeContent(value);
      if (sanitizedContent !== value) {
        onChange?.(sanitizedContent);
      }
    }
    onBlur?.();
  }, [value, sanitizeContent, onChange, onBlur]);

  const containerClassName = clsx(
    styles.editorContainer,
    {
      [styles.error]: hasError,
      [styles.disabled]: disabled,
      [styles.readOnly]: readOnly,
    },
    className
  );

  const editorStyle = {
    "--editor-min-height": `${minHeight}px`,
    "--editor-max-height": `${maxHeight}px`,
  } as React.CSSProperties;

  return (
    <div className={styles.editorWrapper}>
      {label && (
        <Text
          variant="small"
          as="label"
          className={clsx(styles.label, labelClassName)}
          htmlFor={editorId}
        >
          {label}
        </Text>
      )}

      <div className={containerClassName} style={editorStyle}>
        <ReactQuill
          id={editorId}
          theme="snow"
          value={value}
          onChange={handleEditorChange}
          onBlur={handleEditorBlur}
          readOnly={disabled || readOnly}
          placeholder={placeholder}
          modules={quillModules}
          formats={quillFormats}
          style={{
            height: "100%",
          }}
        />
      </div>

      {helperText && !hasError && (
        <Text className={styles.helperText} variant="xsmall">
          {helperText}
        </Text>
      )}

      {errors?.map((error) => (
        <Text key={error} className={styles.errorMessage} variant="xsmall">
          {error}
        </Text>
      ))}
    </div>
  );
};
